import type { ComponentProps } from 'react';
import type { Icon as PhosphorIcon, IconProps as PhosphorIconProps } from '@phosphor-icons/react';
import {
  ChartDonutIcon,
  FileTextIcon,
  FunnelIcon,
  GearIcon,
  ImageSquareIcon,
  ListBulletsIcon,
  MegaphoneIcon,
  PaperPlaneTiltIcon,
  ProhibitIcon,
  QuestionIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  SquaresFourIcon,
  UsersIcon,
  UsersThreeIcon,
  KeyIcon,
  ClipboardTextIcon,
} from '@phosphor-icons/react';
import {
  SidebarCampaignIcon,
  SidebarContactsIcon,
  SidebarDashboardIcon,
  SidebarSettingsIcon,
  SidebarTemplatesIcon,
} from '../sidebar-nav-icons/sidebar-nav-icons';

/**
 * MenuIcon Component
 *
 * Maps string names to sidebar navigation icons (Phosphor icons and custom SVGs).
 */

type PhosphorWrapperProps = ComponentProps<'svg'> & {
  size?: number | string;
  weight?: PhosphorIconProps['weight'];
};

const phosphorWrapper = (Icon: PhosphorIcon) => {
  const Wrapped = ({
    size = 24,
    className,
    color,
    weight = 'regular',
    ...rest
  }: PhosphorWrapperProps) => {
    const resolvedSize = typeof size === 'number' ? size : Number(size) || 24;
    const phosphorProps: PhosphorIconProps = {
      size: resolvedSize,
      weight,
      className,
      color: typeof color === 'string' ? color : undefined,
    };
    void rest;
    return <Icon {...phosphorProps} />;
  };
  Wrapped.displayName = `Phosphor(${Icon.displayName ?? 'Icon'})`;
  return Wrapped;
};

const iconMap = {
  Key: phosphorWrapper(KeyIcon),
  SidebarDashboard: SidebarDashboardIcon,
  SidebarCampaign: SidebarCampaignIcon,
  SidebarContacts: SidebarContactsIcon,
  SidebarTemplates: SidebarTemplatesIcon,
  SidebarSettings: SidebarSettingsIcon,
  Users: phosphorWrapper(UsersIcon),
  SquaresFour: phosphorWrapper(SquaresFourIcon),
  ListBullets: phosphorWrapper(ListBulletsIcon),
  Funnel: phosphorWrapper(FunnelIcon),
  Prohibit: phosphorWrapper(ProhibitIcon),
  Megaphone: phosphorWrapper(MegaphoneIcon),
  FileText: phosphorWrapper(FileTextIcon),
  Gear: phosphorWrapper(GearIcon),
  SlidersHorizontal: phosphorWrapper(SlidersHorizontalIcon),
  UsersThree: phosphorWrapper(UsersThreeIcon),
  Question: phosphorWrapper(QuestionIcon),
  ChartDonut: phosphorWrapper(ChartDonutIcon),
  ShieldCheck: phosphorWrapper(ShieldCheckIcon),
  PaperPlaneTilt: phosphorWrapper(PaperPlaneTiltIcon),
  ImageSquare: phosphorWrapper(ImageSquareIcon),
  ClipboardText: phosphorWrapper(ClipboardTextIcon),
} as const;

export type MenuIconName = keyof typeof iconMap;

export interface MenuIconProps extends ComponentProps<'svg'> {
  name: MenuIconName;
  size?: number | string;
  weight?: PhosphorIconProps['weight'];
  strokeWidth?: number | string;
}

export const MenuIcon = ({
  name,
  size = 24,
  strokeWidth,
  className,
  weight,
  ...props
}: Readonly<MenuIconProps>) => {
  const Icon = iconMap[name];
  if (!Icon) return null;

  const resolvedSize = typeof size === 'number' ? size : Number(size) || 24;

  return (
    <Icon
      size={resolvedSize}
      strokeWidth={strokeWidth}
      className={className}
      weight={weight}
      {...props}
    />
  );
};
